import {useCallback, useEffect, useMemo, useState} from "react";
import {Badge, Button, Collapse, Form, Modal, Spinner} from "react-bootstrap";
import {Check, ChevronDown, ChevronUp, Clipboard, MapPin, MessageSquare, RefreshCw} from "lucide-react";

import {addJobTimelineComment, getJobTimeline} from "../service/JobService";
import "./JobTimeline.css";

const CATEGORY_VARIANTS = {
    COMMENT: 'primary',
    GUESTY: 'info',
    IMAGE: 'success',
    LOCATION: 'warning',
    CHECKLIST: 'secondary',
    ISSUE: 'danger',
    PRICING: 'dark',
    JOB: 'secondary',
};

const FRIENDLY_FIELDS = {
    cosJobStatus: 'Status',
    cosJobWorker: 'Field worker',
    cosJobSupervisor: 'Supervisor',
    description: 'Description',
    date: 'Job date',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    completed: 'Completed',
    imageIds: 'Images',
};

export function groupTimelineItems(items = []) {
    return items.reduce((groups, item) => {
        if (item.event_type !== 'IMAGE_UPLOADED') {
            groups.push(item);
            return groups;
        }
        const previous = groups.at(-1);
        const previousItems = previous?.group_type === 'IMAGE_UPLOADS' ? previous.items : null;
        const previousItem = previousItems?.at(-1);
        const sameUploader = previousItem?.operator_username === item.operator_username;
        const withinFiveMinutes = previousItem
            && Math.abs(new Date(previousItem.occurred_at) - new Date(item.occurred_at)) <= 5 * 60 * 1000;
        if (previousItems && sameUploader && withinFiveMinutes) {
            previous.items.push(item);
            previous.title = `${previous.items.length} images uploaded`;
            return groups;
        }
        groups.push({
            ...item,
            key: `IMAGE_GROUP:${item.key}`,
            group_type: 'IMAGE_UPLOADS',
            title: 'Image uploaded',
            items: [item],
        });
        return groups;
    }, []);
}

function formatTime(value) {
    if (!value) return 'Unknown time';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-AU', {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(date);
}

function displayValue(value) {
    if (value === null || value === undefined || value === '') return 'None';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

function EventIcon({category}) {
    if (category === 'LOCATION') return <MapPin size={17} aria-hidden="true"/>;
    if (category === 'COMMENT') return <MessageSquare size={17} aria-hidden="true"/>;
    return <Check size={17} aria-hidden="true"/>;
}

function CopyValue({label, value}) {
    const [copied, setCopied] = useState(false);
    if (value === null || value === undefined || value === '') return null;

    async function copy() {
        await navigator.clipboard.writeText(String(value));
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
    }

    return (
        <div className="job-timeline-reference">
            <span><strong>{label}:</strong> {value}</span>
            <Button variant="link" size="sm" onClick={copy} aria-label={`Copy ${label}`}>
                {copied ? <Check size={14}/> : <Clipboard size={14}/>}
            </Button>
        </div>
    );
}

function TimelineDetails({entry}) {
    const [open, setOpen] = useState(false);
    const details = entry.details || {};
    const changes = entry.changes || [];
    const imageItems = entry.items || [];
    const hasExpandableDetails = changes.length > 0 || imageItems.length > 1;

    return (
        <>
            {details.comment ? <div className="job-timeline-comment">{details.comment}</div> : null}
            {entry.category === 'LOCATION' ? (
                <div className="job-timeline-muted">
                    GPS verified{details.sample_count ? ` with ${details.sample_count} samples` : ''}
                    {details.backfilled ? ' · Backfilled' : ''}
                </div>
            ) : null}
            <CopyValue label="Reservation ID" value={details.reservation_id}/>
            <CopyValue label="Guesty message ID" value={details.message_id}/>

            {hasExpandableDetails ? (
                <>
                    <Button
                        className="job-timeline-details-toggle"
                        variant="link"
                        size="sm"
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                    >
                        {open ? <ChevronUp size={15}/> : <ChevronDown size={15}/>}
                        {open ? 'Hide details' : 'Show details'}
                    </Button>
                    <Collapse in={open}>
                        <div>
                            {changes.map(change => (
                                <div className="job-timeline-change" key={change.field}>
                                    <strong>{FRIENDLY_FIELDS[change.field] || change.field}</strong>
                                    <span>{displayValue(change.old_value)} → {displayValue(change.new_value)}</span>
                                </div>
                            ))}
                            {imageItems.length > 1 ? (
                                <div className="job-timeline-muted">
                                    {imageItems.length} uploads recorded between {formatTime(imageItems.at(-1).occurred_at)} and {formatTime(imageItems[0].occurred_at)}
                                </div>
                            ) : null}
                        </div>
                    </Collapse>
                </>
            ) : null}
        </>
    );
}

function TimelineEntry({entry}) {
    const actor = entry.operator_username
        || (entry.operator_role === 'SYSTEM' ? 'System' : null)
        || (entry.operator_role === 'WORKER' && entry.operator_user_id
            ? `Worker #${entry.operator_user_id}`
            : entry.operator_role);
    return (
        <article className="job-timeline-entry" data-testid="timeline-entry">
            <div className={`job-timeline-marker job-timeline-marker-${(entry.category || 'JOB').toLowerCase()}`}>
                <EventIcon category={entry.category}/>
            </div>
            <div className="job-timeline-card">
                <div className="job-timeline-heading">
                    <div>
                        <strong>{entry.title}</strong>
                        <Badge bg={CATEGORY_VARIANTS[entry.category] || 'secondary'} className="ms-2">
                            {entry.category || 'JOB'}
                        </Badge>
                    </div>
                    <time dateTime={entry.occurred_at}>{formatTime(entry.occurred_at)}</time>
                </div>
                {(actor || entry.source) ? (
                    <div className="job-timeline-muted">
                        {actor ? `By ${actor}` : ''}{actor && entry.source ? ' · ' : ''}{entry.source || ''}
                    </div>
                ) : null}
                <TimelineDetails entry={entry}/>
            </div>
        </article>
    );
}

export default function JobTimeline({jobId}) {
    const [items, setItems] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const [comment, setComment] = useState('');
    const [commentError, setCommentError] = useState('');
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async (cursor = null, append = false) => {
        if (!jobId) return;
        append ? setLoadingMore(true) : setLoading(true);
        setError('');
        try {
            const response = await getJobTimeline(jobId, cursor);
            if (!response.ok) {
                throw new Error(await response.text() || 'Unable to load the timeline');
            }
            const payload = await response.json();
            setItems(current => append ? [...current, ...(payload.items || [])] : (payload.items || []));
            setNextCursor(payload.next_cursor || null);
        } catch (requestError) {
            setError(requestError.message || 'Unable to load the timeline');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [jobId]);

    useEffect(() => {
        load();
    }, [load]);

    const groupedItems = useMemo(() => groupTimelineItems(items), [items]);

    async function submitComment(event) {
        event.preventDefault();
        const value = comment.trim();
        if (!value || submitting) return;
        setSubmitting(true);
        setCommentError('');
        try {
            const response = await addJobTimelineComment(jobId, value);
            if (!response.ok) {
                throw new Error(await response.text() || 'Unable to add the comment');
            }
            const created = await response.json();
            setItems(current => [created, ...current.filter(item => item.key !== created.key)]);
            setComment('');
            setShowCommentModal(false);
        } catch (requestError) {
            setCommentError(requestError.message || 'Unable to add the comment');
        } finally {
            setSubmitting(false);
        }
    }

    function openCommentModal() {
        setComment('');
        setCommentError('');
        setShowCommentModal(true);
    }

    function closeCommentModal() {
        if (submitting) return;
        setComment('');
        setCommentError('');
        setShowCommentModal(false);
    }

    if (!jobId) {
        return <div className="job-timeline-empty">Timeline is available after the Job has been saved.</div>;
    }

    return (
        <section className="job-timeline" aria-label="Job timeline">
            <div className="job-timeline-toolbar">
                <h4>Timeline</h4>
                <div className="job-timeline-toolbar-actions">
                    <Button variant="primary" size="sm" onClick={openCommentModal}>
                        <MessageSquare size={15} className="me-1"/> Add
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => load()} disabled={loading}>
                        <RefreshCw size={15} className="me-1"/> Refresh
                    </Button>
                </div>
            </div>

            <Modal
                show={showCommentModal}
                onHide={closeCommentModal}
                aria-labelledby="timeline-comment-modal-title"
                centered
            >
                <Form onSubmit={submitComment}>
                    <Modal.Header closeButton={!submitting}>
                        <Modal.Title id="timeline-comment-modal-title">
                            Add timeline comment
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {commentError ? <div className="alert alert-danger" role="alert">{commentError}</div> : null}
                        <Form.Label htmlFor="timeline-comment">Comment</Form.Label>
                        <Form.Control
                            id="timeline-comment"
                            as="textarea"
                            rows={4}
                            maxLength={2000}
                            autoFocus
                            value={comment}
                            placeholder="Add operational context…"
                            onChange={event => setComment(event.target.value)}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={closeCommentModal} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!comment.trim() || submitting}>
                            {submitting ? <Spinner size="sm"/> : 'Add'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {error ? <div className="alert alert-danger" role="alert">{error}</div> : null}
            {loading ? (
                <div className="job-timeline-empty"><Spinner size="sm" className="me-2"/>Loading timeline…</div>
            ) : groupedItems.length ? (
                <div className="job-timeline-list">
                    {groupedItems.map(entry => <TimelineEntry key={entry.key} entry={entry}/>) }
                </div>
            ) : (
                <div className="job-timeline-empty">No timeline activity has been recorded for this Job yet.</div>
            )}

            {nextCursor ? (
                <div className="text-center mt-3">
                    <Button variant="outline-primary" onClick={() => load(nextCursor, true)} disabled={loadingMore}>
                        {loadingMore ? <Spinner size="sm"/> : 'Load older activity'}
                    </Button>
                </div>
            ) : null}
        </section>
    );
}
